import type { AnyID } from '@types'
import { isArmCatalogId } from '@utils/linkedArms/armOccupancy'
import { getArmSideSign, resolveLayoutWidth } from '@utils/linkedArms/armPlacement'
import { createArmRowContext, type RowSide } from '@utils/linkedArms/armRowContext'
import { buildItemOBBFromMatrix } from '@utils/sceneObjects/items/itemObbUtils'
import {
  collectArmsAtUnitRowEnd,
  composeMatrixModuleOnRinconeroLeg,
  composeMatrixRinconeroOnUnitEnd,
  composeRinconeroDragRowEndSnap,
  composeStraightModuleSnapOnRinconero,
  inspectRinconeroFaceOccupancy,
  isCornerXLBrazoCatalog,
  isModularCornerSnapCatalog,
  isOreja,
  isRinconeroCatalog,
  isRinconeroIzquierda,
  RINCONERO_DRAW_REMOVE_ARM_NOTICE,
  rinconeroBaseCatalogCode,
  rinconeroHandednessFromItem,
  rinconeroPlanFondo,
  rinconeroPlanWidth,
  rinconeroSnapSideFromFace,
} from '@utils/sceneObjects/items/rinconeroUtils'
import { Matrix4, Quaternion, Vector3 } from 'three'
import { OBB } from 'three/examples/jsm/math/OBB.js'

export const CORNER_INSERT_NO_FREE_END_NOTICE =
  'No hay un extremo libre compatible para esta unidad Corner.'

type SceneUnit = {
  id?: AnyID
  type?: string
  catalogID?: unknown
  width?: number
  height?: number
  thickness?: number
  matrix?: Matrix4 | { decompose: (p: Vector3, q: Quaternion, s: Vector3) => void }
  visible?: boolean
  firstParentID?: AnyID
}

export type CornerInsertPlan = {
  matrix: Matrix4
  endUnitId: AnyID
  endUnit: SceneUnit
  attachEnd: 'left' | 'right'
  armIdsToRemove: AnyID[]
  reason: string
}

const OBB_CONTACT_SHRINK_MM = 2
/** Holgura en planta para detectar BR entre Corner y módulo (mm). */
const ARM_JUNCTION_SLAB_PAD_MM = 80
const ARM_JUNCTION_DEPTH_TOLERANCE_MM = 420

/** Lado del brazo respecto al módulo; evita clasificar mal el flush izquierdo (dot≈0 → derecha). */
function resolveArmSideOnUnit(unit: SceneUnit, arm: SceneUnit): RowSide {
  if (!unit.matrix || !arm.matrix) return getArmSideSign(unit as any, arm as any)
  const unitPos = getWorldPos(unit)
  const unitQuat = new Quaternion()
  unit.matrix.decompose(new Vector3(), unitQuat, new Vector3())
  const unitX = new Vector3(1, 0, 0).applyQuaternion(unitQuat)
  const armPos = getWorldPos(arm)
  const proj = armPos.clone().sub(unitPos).dot(unitX)
  const unitWidth = resolveLayoutWidth(unit as any) || Number(unit.width || 0)
  const armWidth = resolveLayoutWidth(arm as any) || Number(arm.width || 0)
  const expectedRight = unitWidth
  const expectedLeft = -armWidth
  return Math.abs(proj - expectedRight) <= Math.abs(proj - expectedLeft) ? 1 : -1
}

function armSceneId(item: SceneUnit, mapKey: string | number): AnyID {
  return (item.id ?? mapKey) as AnyID
}

/** Catálogos que deben insertarse solo en el extremo de composición (166 / oreja / Corner XL). */
export function isCornerInsertCatalog(catalogID: unknown): boolean {
  return isModularCornerSnapCatalog(catalogID) || isOreja(catalogID)
}

/**
 * Extremo natural según mano del catálogo.
 * Oreja: …i → left, …d → right. 166 / Corner XL: isRinconeroIzquierda.
 */
export function resolveCornerDrawAttachEnd(drawingCatalog: unknown): 'left' | 'right' | null {
  if (isOreja(drawingCatalog)) {
    const base = rinconeroBaseCatalogCode(drawingCatalog)
    if (base.endsWith('i')) return 'left'
    if (base.endsWith('d')) return 'right'
    return null
  }
  if (!isModularCornerSnapCatalog(drawingCatalog)) return null
  const hand = rinconeroHandednessFromItem({ catalogID: drawingCatalog })
  return isRinconeroIzquierda(drawingCatalog, hand) ? 'left' : 'right'
}

function sideFromAttachEnd(end: 'left' | 'right'): RowSide {
  return end === 'right' ? 1 : -1
}

/**
 * Brazos atrapados en la junta Corner↔módulo (proyección en eje X del ancla).
 * Cubre el caso en que el BR queda entre ambas unidades tras el dibujo.
 */
export function collectArmsInCornerJunction(
  items: Map<string, SceneUnit>,
  endUnit: SceneUnit,
  attachEnd: 'left' | 'right',
  cornerMatrix?: Matrix4 | null,
  cornerWidth = 0,
): AnyID[] {
  const ids = new Set<AnyID>()
  if (!endUnit?.matrix) return []

  const endPos = getWorldPos(endUnit)
  const endQuat = new Quaternion()
  endUnit.matrix.decompose(new Vector3(), endQuat, new Vector3())
  const endX = new Vector3(1, 0, 0).applyQuaternion(endQuat)
  const endZ = new Vector3(0, 0, 1).applyQuaternion(endQuat)
  const endWidth = resolveLayoutWidth(endUnit as any) || Number(endUnit.width || 0)

  // Cara de contacto del módulo (origen izq / borde der).
  const faceT = attachEnd === 'right' ? endWidth : 0
  let slabMin = faceT - ARM_JUNCTION_SLAB_PAD_MM
  let slabMax = faceT + ARM_JUNCTION_SLAB_PAD_MM

  if (cornerMatrix && cornerWidth > 0) {
    const cornerPos = new Vector3()
    cornerMatrix.decompose(cornerPos, new Quaternion(), new Vector3())
    const cornerT0 = cornerPos.clone().sub(endPos).dot(endX)
    const cornerT1 = cornerT0 + cornerWidth
    const junctionMin = Math.min(faceT, cornerT0, cornerT1) - ARM_JUNCTION_SLAB_PAD_MM
    const junctionMax = Math.max(faceT, cornerT0, cornerT1) + ARM_JUNCTION_SLAB_PAD_MM
    slabMin = junctionMin
    slabMax = junctionMax
  } else if (attachEnd === 'left') {
    // Sin matriz aún: franja exterior a la izquierda del origen.
    slabMin = -Math.max(cornerWidth, 600) - ARM_JUNCTION_SLAB_PAD_MM
    slabMax = ARM_JUNCTION_SLAB_PAD_MM
  } else {
    slabMin = endWidth - ARM_JUNCTION_SLAB_PAD_MM
    slabMax = endWidth + Math.max(cornerWidth, 600) + ARM_JUNCTION_SLAB_PAD_MM
  }

  items.forEach((item, mapKey) => {
    if (!item?.matrix || !isArmCatalogId(item.catalogID)) return
    const armPos = getWorldPos(item)
    const delta = armPos.clone().sub(endPos)
    if (Math.abs(delta.dot(endZ)) > ARM_JUNCTION_DEPTH_TOLERANCE_MM) return
    if (Math.abs(delta.y) > 400) return
    const t = delta.dot(endX)
    if (t < slabMin || t > slabMax) return
    ids.add(armSceneId(item, mapKey))
  })

  return Array.from(ids)
}

/**
 * Brazos que ocupan el extremo donde se insertará la Corner.
 * Unión de: parent+lado, junta geométrica, collector legacy.
 * La regla de negocio es eliminarlos, nunca migrarlos.
 */
export function collectArmsAtCornerInsertEnd(
  items: Map<string, SceneUnit>,
  endUnit: SceneUnit,
  attachEnd: 'left' | 'right',
  cornerMatrix?: Matrix4 | null,
  cornerWidth = 0,
): AnyID[] {
  const ids = new Set<AnyID>()
  if (!endUnit?.matrix) return []

  const endUnitId = String(endUnit.id ?? '')
  const side = sideFromAttachEnd(attachEnd)

  // 1) Brazos hijos del extremo en ese lado (lado resuelto por flush, no solo dot).
  items.forEach((item, mapKey) => {
    if (!item?.matrix || !isArmCatalogId(item.catalogID)) return
    const parentId = String(item.firstParentID || '')
    if (endUnitId && parentId === endUnitId && resolveArmSideOnUnit(endUnit, item) === side) {
      ids.add(armSceneId(item, mapKey))
    }
  })

  // 2) Contexto de fila: mismo criterio que labios exteriores.
  if (endUnitId) {
    const ctx = createArmRowContext(items as Map<AnyID, any>, endUnit, { getArmSideSign })
    for (const arm of ctx.getArmsOnUnit(endUnitId)) {
      if (resolveArmSideOnUnit(endUnit, arm) === side) {
        ids.add(arm.id as AnyID)
      }
    }
  }

  // 3) Junta geométrica Corner↔módulo (incluye BR huérfanos / mal parentados).
  collectArmsInCornerJunction(items, endUnit, attachEnd, cornerMatrix, cornerWidth).forEach((id) =>
    ids.add(id),
  )

  // 4) Collector legacy.
  if (endUnit.id != null) {
    collectArmsAtUnitRowEnd(
      items as Map<string, any>,
      endUnit.id as AnyID,
      endUnit,
      attachEnd,
    ).forEach((id) => ids.add(id))
  }

  return Array.from(ids)
}

/** Elimina los brazos del extremo Corner (IDs ya resueltos). */
export function removeArmsForCornerInsert(
  armIds: AnyID[],
  removeIds: (ids: AnyID[]) => void,
  notify: (message: string) => void,
): void {
  const unique = Array.from(new Set(armIds.map((id) => id).filter(Boolean)))
  if (!unique.length) return
  notify(RINCONERO_DRAW_REMOVE_ARM_NOTICE)
  removeIds(unique)
}

function getWorldPos(unit: SceneUnit): Vector3 {
  const pos = new Vector3()
  if (!unit.matrix) return pos
  unit.matrix.decompose(pos, new Quaternion(), new Vector3())
  return pos
}

function toMatrix4(matrix: SceneUnit['matrix']): Matrix4 | null {
  if (!matrix) return null
  if (matrix instanceof Matrix4) return matrix
  const pos = new Vector3()
  const quat = new Quaternion()
  const scale = new Vector3()
  matrix.decompose(pos, quat, scale)
  return new Matrix4().compose(pos, quat, scale)
}

function proposedPosePenetrates(
  matrix: Matrix4,
  drawWidth: number,
  drawThickness: number,
  drawHeight: number,
  items: Map<string, SceneUnit>,
  endUnitId: AnyID,
): boolean {
  if (!(drawWidth > 0) || !(drawThickness > 0)) return false
  const height = drawHeight > 0 ? drawHeight : 800
  const proposed = buildItemOBBFromMatrix(matrix, drawWidth, height, drawThickness)
  proposed.halfSize.x = Math.max(1, proposed.halfSize.x - OBB_CONTACT_SHRINK_MM)
  proposed.halfSize.y = Math.max(1, proposed.halfSize.y - OBB_CONTACT_SHRINK_MM)
  proposed.halfSize.z = Math.max(1, proposed.halfSize.z - OBB_CONTACT_SHRINK_MM)

  const otherObb = new OBB()
  let hits = false
  items.forEach((candidate, candidateId) => {
    if (hits) return
    if (String(candidateId) === String(endUnitId)) return
    if (candidate?.type !== 'linkedItem' || candidate.visible === false || !candidate.matrix) return
    if (isArmCatalogId(candidate.catalogID)) return

    const candMatrix = toMatrix4(candidate.matrix)
    if (!candMatrix) return
    const w = rinconeroPlanWidth(candidate) || Number(candidate.width || 0)
    const t = Number(candidate.thickness || 0) || rinconeroPlanFondo(candidate) || 0
    const h = Number(candidate.height || 0) || height
    if (!(w > 0) || !(t > 0)) return

    buildItemOBBFromMatrix(candMatrix, w, h, t, otherObb)
    otherObb.halfSize.x = Math.max(1, otherObb.halfSize.x - OBB_CONTACT_SHRINK_MM)
    otherObb.halfSize.y = Math.max(1, otherObb.halfSize.y - OBB_CONTACT_SHRINK_MM)
    otherObb.halfSize.z = Math.max(1, otherObb.halfSize.z - OBB_CONTACT_SHRINK_MM)
    if (proposed.intersectsOBB(otherObb)) hits = true
  })
  return hits
}

function composeCornerMatrixOnEnd(
  endUnit: SceneUnit,
  drawingCatalog: unknown,
  attachEnd: 'left' | 'right',
  drawWidth: number,
): Matrix4 | null {
  if (!endUnit.matrix) return null

  if (isCornerXLBrazoCatalog(drawingCatalog)) {
    const endPos = getWorldPos(endUnit)
    const endQuat = new Quaternion()
    endUnit.matrix.decompose(new Vector3(), endQuat, new Vector3())
    const endX = new Vector3(1, 0, 0).applyQuaternion(endQuat)
    const endWidth = rinconeroPlanWidth(endUnit) || Number(endUnit.width || 0)
    const probeAlongX = attachEnd === 'right' ? endWidth + 300 : -300
    const probePos = endPos.clone().add(endX.multiplyScalar(probeAlongX))
    return composeRinconeroDragRowEndSnap(
      endUnit as any,
      {
        catalogID: drawingCatalog,
        width: drawWidth,
        thickness: endUnit.thickness,
      },
      probePos,
    )
  }

  return composeMatrixRinconeroOnUnitEnd(
    endUnit as any,
    attachEnd,
    attachEnd === 'left' ? drawWidth : 0,
  )
}

function defaultCornerWidth(catalogID: unknown): number {
  return rinconeroPlanWidth({ catalogID }) || 0
}

/**
 * Extremos de fila por proyección local.
 * Solo unidades rectas; el lado pedido debe estar libre de vecino en fila.
 */
function resolveFallbackRowEnds(row: any[], requiredSide: RowSide): SceneUnit[] {
  const ends: SceneUnit[] = []
  for (const unit of row) {
    if (!unit?.matrix || !unit?.id) continue
    if (isRinconeroCatalog(unit.catalogID) || isCornerInsertCatalog(unit.catalogID)) continue
    if (isArmCatalogId(unit.catalogID)) continue

    const unitPos = getWorldPos(unit)
    const unitQuat = new Quaternion()
    unit.matrix.decompose(new Vector3(), unitQuat, new Vector3())
    const unitX = new Vector3(1, 0, 0).applyQuaternion(unitQuat)
    const unitZ = new Vector3(0, 0, 1).applyQuaternion(unitQuat)
    const unitWidth = rinconeroPlanWidth(unit) || Number(unit.width || 0)

    let hasNeighborOnSide = false
    for (const other of row) {
      if (!other?.matrix || String(other.id) === String(unit.id)) continue
      if (isArmCatalogId(other.catalogID)) continue
      const otherPos = getWorldPos(other)
      const delta = otherPos.clone().sub(unitPos)
      if (Math.abs(delta.dot(unitZ)) > 120) continue
      const sideProj = delta.dot(unitX)
      const otherWidth = rinconeroPlanWidth(other) || Number(other.width || 0)
      if (requiredSide === 1 && Math.abs(sideProj - unitWidth) <= 80) {
        hasNeighborOnSide = true
        break
      }
      if (requiredSide === -1 && Math.abs(sideProj + otherWidth) <= 80) {
        hasNeighborOnSide = true
        break
      }
    }
    if (!hasNeighborOnSide) ends.push(unit)
  }
  return ends
}

/**
 * 166 solo o con una sola cara libre: permite Corner/oreja en esa cara.
 * Nunca elige una cara ya ocupada por 160.
 */
function resolveCornerOnFreeRinconeroFace(
  items: Map<string, SceneUnit>,
  row: any[],
  drawingCatalog: unknown,
  attachEnd: 'left' | 'right',
  width: number,
  thickness: number,
  height: number,
  anchorPos: Vector3,
): CornerInsertPlan | null {
  const rinconeros = row
    .filter((u) => u?.matrix && u?.id && isRinconeroCatalog(u.catalogID) && !isOreja(u.catalogID))
    .sort((a, b) => getWorldPos(a).distanceTo(anchorPos) - getWorldPos(b).distanceTo(anchorPos))

  for (const rin of rinconeros) {
    const occ = inspectRinconeroFaceOccupancy(
      String(rin.id),
      rin,
      items as Map<string, any>,
      [],
      width,
    )
    if (occ.bothExteriorFacesOccupied) continue

    const legSide = rinconeroSnapSideFromFace(rin, 'leg')
    const lateralSide = rinconeroSnapSideFromFace(rin, 'lateral')
    const openLateralOccupied = occ.lateralAfter || occ.lateralBefore

    let face: 'leg' | 'lateral' | null = null
    if (attachEnd === legSide && !occ.legOccupied) face = 'leg'
    else if (attachEnd === lateralSide && !openLateralOccupied) face = 'lateral'
    if (!face) continue

    let matrix: Matrix4 | null = null
    if (
      isModularCornerSnapCatalog(drawingCatalog) &&
      !isOreja(drawingCatalog) &&
      face === 'leg'
    ) {
      matrix = composeMatrixModuleOnRinconeroLeg(rin, width, thickness)
    } else {
      const snap = composeStraightModuleSnapOnRinconero(rin, width, attachEnd, thickness)
      matrix = snap?.matrix ?? null
    }
    if (!matrix) continue
    if (proposedPosePenetrates(matrix, width, thickness, height, items, rin.id as AnyID)) {
      continue
    }

    return {
      matrix,
      endUnitId: rin.id as AnyID,
      endUnit: rin,
      attachEnd,
      armIdsToRemove: collectArmsAtCornerInsertEnd(items, rin, attachEnd, matrix, width),
      reason: face === 'leg' ? 'rinconero-free-leg' : 'rinconero-free-lateral',
    }
  }

  return null
}

/**
 * Plan de inserción Corner/oreja sobre una composición ya formada (p. ej. 166 + 160).
 *
 * Reglas:
 * - Solo extremo exterior compatible con la mano del catálogo.
 * - Nunca sobre una cara ya ocupada del 166 (pata/lateral con 160).
 * - Nunca en el interior de la fila.
 * - Elimina BRs del extremo destino.
 * - Rechaza poses que penetren otras unidades.
 */
export function resolveCornerInsertPlan(
  items: Map<string, SceneUnit>,
  anchorUnit: SceneUnit,
  drawingCatalog: unknown,
  drawWidth: number,
  drawThickness = 0,
  drawHeight = 0,
): CornerInsertPlan | null {
  if (!anchorUnit?.matrix || !isCornerInsertCatalog(drawingCatalog)) return null

  const attachEnd = resolveCornerDrawAttachEnd(drawingCatalog)
  if (!attachEnd) return null

  const requiredSide = sideFromAttachEnd(attachEnd)
  const width = drawWidth > 0 ? drawWidth : defaultCornerWidth(drawingCatalog)
  if (!(width > 0)) return null

  const ctx = createArmRowContext(items as Map<AnyID, any>, anchorUnit, { getArmSideSign })
  const anchorPos = getWorldPos(anchorUnit)

  // Con 166+160, destino = tip libre de la cadena 160 (no la cara del 166).
  const matchingLips = ctx
    .listExteriorLips()
    .filter((lip) => lip.side === requiredSide && lip.unit?.matrix && lip.unit?.id != null)
    .filter(
      (lip) =>
        !isRinconeroCatalog(lip.unit.catalogID) && !isCornerInsertCatalog(lip.unit.catalogID),
    )
    .sort(
      (a, b) =>
        getWorldPos(a.unit).distanceTo(anchorPos) - getWorldPos(b.unit).distanceTo(anchorPos),
    )

  if (matchingLips.length === 0 && ctx.row.length > 0) {
    for (const endUnit of resolveFallbackRowEnds(ctx.row, requiredSide)) {
      matchingLips.push({ unit: endUnit, side: requiredSide })
    }
  }

  const thickness =
    drawThickness ||
    rinconeroPlanFondo({ catalogID: drawingCatalog, thickness: anchorUnit.thickness }) ||
    0
  const height = drawHeight || Number(anchorUnit.height || 0)

  for (const lip of matchingLips) {
    const endUnit = lip.unit as SceneUnit
    const endUnitId = endUnit.id as AnyID
    const matrix = composeCornerMatrixOnEnd(endUnit, drawingCatalog, attachEnd, width)
    if (!matrix) continue
    if (proposedPosePenetrates(matrix, width, thickness, height, items, endUnitId)) continue

    return {
      matrix,
      endUnitId,
      endUnit,
      attachEnd,
      armIdsToRemove: collectArmsAtCornerInsertEnd(
        items,
        endUnit,
        attachEnd,
        matrix,
        width,
      ),
      reason: 'composition-exterior-end',
    }
  }

  // Sin tip 160: solo cara libre del 166. Si ambas caras tienen 160 → null.
  return resolveCornerOnFreeRinconeroFace(
    items,
    ctx.row,
    drawingCatalog,
    attachEnd,
    width,
    thickness,
    height,
    anchorPos,
  )
}
